<template>
  <div class="basic-page">
    <header class="basic-header">
      <div class="basic-header-avatar">
        <img src="@/assets/patient/patient_cal.png" alt="">
      </div>
      <div class="basic-header-text">
        <div class="basic-header-date">{{ title_today }}</div>
        <div class="basic-header-name">{{ patientName }}</div>
      </div>
    </header>

    <el-tabs v-model="activeName" class="basic-tabs" @tab-click="handleClick">
      <el-tab-pane label="基本信息" name="first" :lazy="true" class="basic-tab-pane">
        <div class="basic-stack">
          <el-card class="basic-card basic-card--profile box-card" shadow="hover">
            <div slot="header" class="clearfix card-header basic-card-head">
              <i class="el-icon-card-header"></i>
              <span>基本信息</span>
            </div>
            <div v-if="profileEntries.length > 0" class="basic-profile-grid">
              <div
                v-for="(pair, rowIdx) in profileEntryRows"
                :key="'profile-row-' + rowIdx"
                class="basic-profile-row"
              >
                <div
                  v-for="item in pair"
                  :key="item.key"
                  class="basic-profile-pair"
                >
                  <span class="basic-profile-key">{{ item.key }}</span>
                  <span class="basic-profile-val">{{ item.displayValue }}</span>
                </div>
              </div>
            </div>
            <div v-else class="basic-profile-empty-wrap">
              <el-empty description="暂无数据" />
            </div>
          </el-card>

          <el-card class="basic-card box-card" shadow="hover">
            <div slot="header" class="clearfix card-header basic-card-head">
              <i class="el-icon-card-header"></i>
              <span>就诊分析</span>
            </div>
            <el-row :gutter="16" class="basic-chart-row">
              <el-col v-for="chart in treatPieCharts" :key="chart.id" :xs="24" :sm="12" :md="8">
                <div class="basic-chart-cell">
                  <PieChart
                    :id="chart.id"
                    :params="patientParams"
                    height="300px"
                    width="100%"
                    :path="chart.path"
                  />
                </div>
              </el-col>
            </el-row>
          </el-card>

          <el-card class="basic-card box-card" shadow="hover">
            <div slot="header" class="clearfix card-header basic-card-head">
              <i class="el-icon-card-header"></i>
              <span>就诊消费</span>
            </div>
            <div class="basic-line-wrap">
              <LineChart
                id="user_treat_hist_line_ele"
                path="user_treat_hist_line"
                height="300px"
                width="100%"
                :params="patientParams"
              />
            </div>
          </el-card>

          <el-card class="basic-card box-card basic-card--table" shadow="hover">
            <div slot="header" class="clearfix card-header basic-card-head">
              <i class="el-icon-card-header"></i>
              <span>患者跟踪</span>
            </div>
            <el-table
              class="basic-table"
              :data="user_treat_hist"
              :header-cell-style="tableHeaderStyle"
              :row-class-name="tableRowClassName"
            >
              <el-table-column
                v-for="column in treatTableColumns"
                :key="column.prop"
                v-bind="column"
              ></el-table-column>
            </el-table>
          </el-card>
        </div>
      </el-tab-pane>

      <el-tab-pane label="电子处方" name="second" class="basic-tab-pane">
        <div v-if="hasMrBrief" class="basic-rx-panel">
          <div class="basic-swiper-toolbar">
            <div class="basic-rx-carousel" role="group" aria-label="病历切换">
              <div class="basic-rx-arrow-col">
                <button
                  type="button"
                  class="basic-rx-arrow basic-rx-arrow--prev"
                  aria-label="上一份病历"
                  @click="rxSwiperPrev"
                />
              </div>
              <div class="basic-rx-swiper-viewport">
                <swiper ref="rxSwiper" class="swiper rx-swiper-inner" :options="swiperOption">
                  <swiper-slide
                    v-for="item in patient_mr_brief_data"
                    :key="item.mr_id"
                    class="swiper-slide-item"
                    :class="{ 'swiper-slide-selected': item.mr_id === sel_mr_id }"
                  >
                    <div
                      role="button"
                      tabindex="0"
                      @click="set_mr_id(item.mr_id)"
                      :id="'div_' + item.mr_id"
                      class="swiper-card"
                    >
                      <div class="swiper-card-title">{{ item.mr_diagnosis }}</div>
                      <div class="swiper-card-time">{{ item.mr_visit_time }}</div>
                    </div>
                  </swiper-slide>
                </swiper>
              </div>
              <div class="basic-rx-arrow-col">
                <button
                  type="button"
                  class="basic-rx-arrow basic-rx-arrow--next"
                  aria-label="下一份病历"
                  @click="rxSwiperNext"
                />
              </div>
            </div>
          </div>
          <div class="basic-detail-wrap">
            <MedicalRecordDetail :mr_id="sel_mr_id" :key="sel_mr_id" />
          </div>
        </div>
        <div v-else class="basic-empty-wrap">
          <el-empty description="暂无电子处方" />
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import {getTime} from "@/utils";
import PieChart from "@/views/charts/pie";
import LineChart from "@/views/charts/line";
import '@/assets/css/main.css';
import {Role} from "@/role";
import {Swiper, SwiperSlide} from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'
import MedicalRecordDetail from "@/views/patient/MedicalRecordDetail";

// API 基地址
const API_HOST = process.env.VUE_APP_API_HOST;
// 日期字段需要格式化展示
const PROFILE_DATE_KEYS = ['最早问诊', '最近一次问诊'];
// Demo 角色需要脱敏的字段
const PROFILE_MASK_KEYS = ['电话号码', '出生日期', '年龄'];
// 就诊分析图表配置
const TREAT_PIE_CHARTS = [
  {id: 'user_treat_hist_stat_pie', path: 'user_treat_hist_stat_pie'},
  {id: 'user_treat_hist_stat_clinic_pie', path: 'user_treat_hist_stat_clinic_pie'},
  {id: 'user_treat_hist_stat_drug_pie', path: 'user_treat_hist_stat_drug_pie'}
];
// 就诊跟踪表格列配置
const TREAT_TABLE_COLUMNS = [
  {prop: '病症', label: '病症'},
  {prop: '医生名称', label: '医生名称', showOverflowTooltip: true},
  {prop: 'clinic_name', label: '诊所名称', showOverflowTooltip: true},
  {prop: '时间', label: '时间'},
  {prop: 'total_fee', label: '总花费（元）'}
];
// 表格表头样式统一
const TABLE_HEADER_STYLE = {background: '#363944', color: 'white', border: 'none'};
/**
 * Swiper 配置：电子处方横向病历卡片。
 * spaceBetween：卡片间距（px）；不再使用内置 navigation，由两侧按钮调用实例方法翻页，避免箭头叠在内容上。
 */
const SWIPER_OPTION = {
  slidesPerView: 5,
  spaceBetween: 14,
  slideToClickedSlide: true,
};

export default {
  name: "Basic",
  components: {
    PieChart,
    LineChart,
    Swiper,
    SwiperSlide,
    MedicalRecordDetail,
  },
  computed: {
    // Demo 角色用于脱敏展示
    isDemo: function () {
      return this.$store.getters.user_role === Role.Demo
    },
    // 是否存在电子处方列表
    hasMrBrief() {
      return Array.isArray(this.patient_mr_brief_data) && this.patient_mr_brief_data.length > 0;
    },
    // 顶部显示的患者姓名
    patientName() {
      return this.user_profile_data ? (this.user_profile_data['姓名'] || '') : '';
    },
    // 基本信息列表（带脱敏/格式化）
    profileEntries() {
      if (!this.user_profile_data) {
        return [];
      }
      return Object.keys(this.user_profile_data).map((key) => ({
        key,
        value: this.user_profile_data[key],
        displayValue: this.formatProfileValue(key, this.user_profile_data[key])
      }));
    },
    /**
     * 将基本信息条目按「每行两条」分组，用于网格展示。
     * 入参：无（依赖计算属性 profileEntries）。
     * 返回：二维数组，内层每组最多 2 条 { key, value, displayValue }。
     */
    profileEntryRows() {
      const list = this.profileEntries || [];
      const rows = [];
      for (let i = 0; i < list.length; i += 2) {
        rows.push(list.slice(i, i + 2));
      }
      return rows;
    },
    // 统一给图表传参
    patientParams() {
      return {patient_id: this.patient_id};
    }
  },
  data() {
    return {
      // 电子处方选中的病历 ID
      sel_mr_id: "",
      // 当前激活的 tab
      activeName: "first",
      // 用户基础信息
      user_profile_data: null,
      // 就诊跟踪列表
      user_treat_hist: [],
      // 电子处方列表
      patient_mr_brief_data: [],
      avg_temp: null,
      yinqing_dict: null,
      // 顶部日期文案
      title_today: '2021-01-01',
      // 路由/本地缓存中的患者信息
      patient_id: this.$route.query.patient_id ? this.$route.query.patient_id : (localStorage.getItem('user_id') ? localStorage.getItem('user_id') : '651'),
      city_id: this.$route.query.city_id ? this.$route.query.city_id : '637377',
      city_name: this.$route.query.city_name ? this.$route.query.city_name : '怀化市',
      // 视图配置
      treatPieCharts: TREAT_PIE_CHARTS,
      treatTableColumns: TREAT_TABLE_COLUMNS,
      tableHeaderStyle: TABLE_HEADER_STYLE,
      // 懒加载标记
      load: {
        first: true,
        second: false,
      },
      // swiper 配置
      swiperOption: SWIPER_OPTION,
    }
  },
  mounted() {
    // 初始化数据
    this.get_user_profile();
    this.get_user_treat_hist();
    this.load_patient_mr_brief(this.patient_id);
    this.title_today = getTime();
  },

  methods: {
    // 切换处方详情
    set_mr_id(mr_id) {
      this.sel_mr_id = mr_id
    },
    /**
     * 电子处方横向列表：取 Swiper 实例（vue-awesome-swiper 挂载在 ref 组件的 swiper 上）。
     * @returns {object|null} Swiper 实例或 null
     */
    rxSwiperInstance() {
      const comp = this.$refs.rxSwiper;
      if (!comp) {
        return null;
      }
      return comp.swiper || comp.$swiper || null;
    },
    /**
     * 上一份病历卡片（仅控制 Swiper 位移，不修改业务数据）。
     */
    rxSwiperPrev() {
      const s = this.rxSwiperInstance();
      if (s) {
        s.slidePrev();
      }
    },
    /**
     * 下一份病历卡片（仅控制 Swiper 位移，不修改业务数据）。
     */
    rxSwiperNext() {
      const s = this.rxSwiperInstance();
      if (s) {
        s.slideNext();
      }
    },
    // 获取电子处方列表
    load_patient_mr_brief(patient_id) {
      this.$http.get(API_HOST + "/patient_mr_brief?patient_id=" + patient_id).then((res) => {
        this.patient_mr_brief_data = res.data.data
        if (res.data.data.length > 0) {
          this.sel_mr_id = res.data.data[0].mr_id
        }
      })
    },
    // tab 点击后标记为已加载
    handleClick(tab) {
      if (this.load[tab.name] === false) {
        this.load[tab.name] = true
      }
    },
    // 表格斑马纹样式
    tableRowClassName({rowIndex}) {
      return rowIndex % 2 === 1 ? 'warning-row1' : 'success-row1';
    },
    // 基本信息字段格式化/脱敏
    formatProfileValue(key, value) {
      if (value === null || value === undefined) {
        return '';
      }
      if (PROFILE_DATE_KEYS.includes(key)) {
        return String(value).split('T').join(' ');
      }
      if (this.isDemo && PROFILE_MASK_KEYS.includes(key)) {
        return "******";
      }
      return value;
    },
    // 获取用户基础信息
    get_user_profile() {
      this.$http.get(API_HOST + "/user_profile?patient_id=" + this.patient_id).then((res) => {
        const d = res.data && res.data.data;
        this.user_profile_data = d != null && typeof d === 'object' ? d : null;
      }).catch(() => {
        this.user_profile_data = null;
      });
    },
    // 获取就诊跟踪数据
    get_user_treat_hist() {
      this.$http.get(API_HOST + "/user_treat_hist?patient_id=" + this.patient_id).then((res) => {
        this.user_treat_hist = res.data.data
      })
    },
  }
}
</script>

<style scoped>
/* 页面容器：统一内边距与最大宽度，模块纵向间距 */
.basic-page {
  min-height: 100%;
  padding: 0 24px 32px;
  box-sizing: border-box;
  max-width: 1400px;
  margin: 0 auto;
}

/* 顶栏：患者概览 */
.basic-header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 4px 22px;
  margin-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.basic-header-avatar {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(104, 245, 185, 0.28);
  background: linear-gradient(145deg, rgba(104, 245, 185, 0.15), rgba(60, 63, 74, 0.9));
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.22);
}

.basic-header-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.basic-header-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.basic-header-date {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.52);
  letter-spacing: 0.03em;
}

.basic-header-name {
  font-size: 21px;
  font-weight: 700;
  color: #f5f7fb;
  letter-spacing: 0.02em;
  line-height: 1.28;
}

/* Tab 区域 */
.basic-tabs {
  margin-top: 10px;
  margin-left: 0;
}

.basic-tabs :deep(.el-tabs__header) {
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.basic-tabs :deep(.el-tabs__nav-wrap::after) {
  display: none;
}

.basic-tab-pane {
  padding-top: 4px;
}

.basic-stack {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.basic-empty-wrap {
  padding: 56px 16px 48px;
}

.basic-empty-wrap :deep(.el-empty__description) {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
}

.basic-empty-wrap :deep(.el-empty__image) {
  opacity: 0.9;
}

/* 卡片统一外观 */
.basic-card {
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  box-shadow: 0 8px 26px rgba(0, 0, 0, 0.18);
  transition: border-color 0.2s ease, box-shadow 0.25s ease;
}

.basic-card:hover {
  border-color: rgba(104, 245, 185, 0.14);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
}

.basic-card :deep(.el-card__header) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  padding: 15px 22px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0.1) 100%);
}

.basic-card :deep(.el-card__body) {
  background: #363944;
  padding: 20px 22px 22px;
}

.basic-card-head {
  border: none;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.02em;
}

.basic-card-head .el-icon-card-header {
  font-size: 16px;
  color: #68f5b9;
}

/* 基本信息卡片：内容区略收紧，整体更紧凑 */
.basic-card--profile :deep(.el-card__body) {
  padding: 14px 18px 16px;
}

.basic-profile-empty-wrap {
  padding: 28px 12px 20px;
}

.basic-profile-empty-wrap :deep(.el-empty__description) {
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
}

.basic-profile-empty-wrap :deep(.el-empty__image) {
  opacity: 0.85;
}

/* 基本信息：每行两条（每行两列「标签 + 值」），无块级底色 */
.basic-profile-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.basic-profile-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 20px;
  align-items: baseline;
}

.basic-profile-pair {
  display: grid;
  grid-template-columns: minmax(64px, 108px) 1fr;
  gap: 2px 8px;
  align-items: start;
}

@media (max-width: 992px) {
  .basic-profile-row {
    grid-template-columns: 1fr;
    gap: 4px 0;
  }

  .basic-profile-pair {
    grid-template-columns: minmax(64px, 96px) 1fr;
  }
}

@media (max-width: 768px) {
  .basic-profile-pair {
    grid-template-columns: minmax(60px, 88px) 1fr;
  }
}

.basic-profile-key {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.35;
  padding-top: 0;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.basic-profile-val {
  font-size: 13px;
  color: #f0f2f6;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
}

/* 图表行 */
.basic-chart-row {
  margin: 0 !important;
}

.basic-chart-cell {
  min-height: 300px;
  margin-bottom: 14px;
  padding: 14px 12px 10px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-sizing: border-box;
}

.basic-chart-cell:last-child {
  margin-bottom: 0;
}

.basic-line-wrap {
  width: 100%;
  min-height: 300px;
  padding: 10px 8px 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-sizing: border-box;
}

.basic-card--table :deep(.el-card__body) {
  padding: 0 0 16px;
}

.basic-card--table :deep(.el-table) {
  border-radius: 0 0 10px 10px;
  overflow: hidden;
}

.basic-table {
  width: 100%;
}

/* 电子处方：上方病历横向切换（仅样式，不改交互与数据） */
.basic-rx-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.basic-swiper-toolbar {
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

/** 左 | 中间滑动区 | 右；箭头独占一列，避免与 Swiper 计算宽度抢空间导致溢出叠盖 */
.basic-rx-carousel {
  display: flex;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  gap: 0;
}

.basic-rx-arrow-col {
  flex: 0 0 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.basic-rx-swiper-viewport {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  padding: 0 6px;
  box-sizing: border-box;
}

/** 外翻页按钮（不占 Swiper 内部宽度） */
.basic-rx-arrow {
  flex-shrink: 0;
  align-self: center;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.basic-rx-arrow:hover {
  background: rgba(104, 245, 185, 0.12);
  border-color: rgba(104, 245, 185, 0.35);
  color: #b8ffe0;
}

.basic-rx-arrow:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(104, 245, 185, 0.35);
}

.basic-rx-arrow::after {
  content: '';
  display: block;
  width: 7px;
  height: 7px;
  margin: 0 auto;
  border: solid currentColor;
  border-width: 0 2px 2px 0;
}

.basic-rx-arrow--prev::after {
  transform: translateX(2px) rotate(135deg);
}

.basic-rx-arrow--next::after {
  transform: translateX(-2px) rotate(-45deg);
}

.basic-detail-wrap {
  min-height: 220px;
  padding-top: 2px;
}

.card-header {
  border: none;
}

:deep(.el-table tbody tr:hover) {
  color: black !important;
}

:deep(.el-table .warning-row1) {
  background: #363944;
}

:deep(.el-table .warning-row1 > td) {
  border: none;
}

:deep(.el-table .success-row1) {
  background: #3c3f4a;
}

:deep(.el-table .success-row1 > td) {
  border: none;
}

:deep(.el-tabs__item) {
  color: rgba(255, 255, 255, 0.62) !important;
  font-size: 14px;
  height: 46px;
  line-height: 46px;
  padding: 0 18px;
}

:deep(.el-tabs__item.is-active) {
  color: #fff !important;
  font-weight: 600;
}

:deep(.el-tabs__active-bar) {
  background-color: #68f5b9 !important;
  height: 3px;
  border-radius: 2px;
}

.swiper.rx-swiper-inner,
.rx-swiper-inner {
  width: 100%;
  max-width: 100%;
}

.swiper {
  height: auto;
  min-height: 56px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
}

.swiper .swiper-slide {
  display: flex;
  justify-content: center;
  align-items: stretch;
  text-align: left;
  font-weight: 500;
  font-size: 12px;
  background: transparent;
  cursor: pointer;
  flex-direction: column;
  height: auto;
  border-radius: 6px;
}

.basic-rx-swiper-viewport :deep(.swiper-container) {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
  overflow: hidden !important;
}

.basic-rx-swiper-viewport :deep(.swiper-wrapper) {
  box-sizing: border-box;
}

/**
 * 勿给 slide 设 min-width/max-width：会与 slidesPerView 的百分比宽度冲突，
 * 导致整块横滑区域溢出，视觉上与两侧箭头「叠在一起」。
 */
.swiper-slide-item {
  box-sizing: border-box;
  height: auto;
}

.swiper-slide-item .swiper-card {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.swiper-slide-item .swiper-card:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.14);
}

/* 选中：色彩明显；阴影仅用内扩，避免外圈与相邻卡片视觉上粘连 */
.swiper-slide-selected .swiper-card {
  background: rgba(104, 245, 185, 0.22);
  border: 1px solid rgba(104, 245, 185, 0.72);
  box-shadow: inset 3px 0 0 #68f5b9;
}

.swiper-slide-selected .swiper-card-title {
  color: #fff;
}

.swiper-slide-selected .swiper-card-time {
  color: rgba(255, 255, 255, 0.6);
}

.swiper-card-title {
  color: #eceef2;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.swiper-card-time {
  color: rgba(255, 255, 255, 0.42);
  font-size: 11px;
  font-weight: 400;
  line-height: 1.25;
}

</style>
